import { IntlMessagesKeys } from '@headless-workspace/typings';
import { HTMLString } from '../../../server';
import { ProductDetailDTO } from './ProductDetailDTO';

export type ManufacturerAdditionalContentsValue = {
    type: 'manufacturer';
    value: ProductDetailDTO['manufacturer'];
};

export type KitAdditionalContentsValue = {
    type: 'kit';
    value: ProductDetailDTO['kit'];
};

export type TabParamsAdditionalContentsValue = (KitAdditionalContentsValue | ManufacturerAdditionalContentsValue)[];

export type ProductInfoTabParamsValue = {
    id: string;
    className: string;
    label: IntlMessagesKeys;
    index?: number;
    displayContentInProductPage?: boolean;
    value: HTMLString;
    additionalContents?: TabParamsAdditionalContentsValue;
};

const createTabParam = (
    id: string,
    className: string,
    label: string,
    value: string | null | undefined,
    displayContentInProductPage?: boolean,
    additionalContents?: TabParamsAdditionalContentsValue,
): ProductInfoTabParamsValue | undefined => {
    if (!value && !value?.length) {
        return;
    }

    return {
        id,
        className,
        label: label as IntlMessagesKeys,
        value: typeof value === 'string' ? HTMLString(value) : value,
        displayContentInProductPage: displayContentInProductPage,
        additionalContents: additionalContents,
    };
};

export const buildAdditionalContents = (
    kit: ProductDetailDTO['kit'],
    manufacturer: ProductDetailDTO['manufacturer'],
    productType: ProductDetailDTO['type'],
): TabParamsAdditionalContentsValue => {
    const additionalContents: TabParamsAdditionalContentsValue = [];
    if (kit && (kit.title || kit.description)) {
        additionalContents.push({ type: 'kit', value: { title: kit.title, description: kit.description } });
    }

    if (productType !== 'PRODUCT_SET') {
        additionalContents.push({
            type: 'manufacturer',
            value: manufacturer,
        });
    }

    return additionalContents;
};

export const mapProductInfo = (dto: ProductDetailDTO): ProductInfoTabParamsValue[] => {
    const tabs = [
        {
            id: 'description',
            className: 'tab-description',
            displayContentInProductPage: true,
            label: 'Discover.Pdp.productInfo.productTab.label.description',
            value: dto.description ?? '',
            additionalContents: buildAdditionalContents(dto.kit, dto.manufacturer, dto.type),
        },
        {
            id: 'tips',
            className: 'tab-tips',
            label: 'Discover.Pdp.productInfo.productTab.label.tips',
            value: dto.tips,
        },
        {
            id: 'testResults',
            className: 'tab-test-results',
            label: 'Discover.Pdp.productInfo.productTab.label.testResults',
            value: dto.testResults,
        },
        {
            id: 'compositions',
            className: 'tab-compositions',
            label: 'Discover.Pdp.productInfo.productTab.label.composition',
            value: dto.compositions,
        },
        {
            id: 'clinicalTests',
            className: 'tab-clinical',
            label: 'Discover.Pdp.productInfo.productTab.label.clinicalTests',
            value: dto.clinicalTests,
        },
        {
            id: 'ingredients',
            className: 'tab-ingredients',
            label: 'Discover.Pdp.productInfo.productTab.label.ingredients',
            value: dto.ingredients,
        },
        {
            id: 'usage',
            className: 'tab-usage',
            label: 'Discover.Pdp.productInfo.productTab.label.usage',
            value: dto.usage,
        },
        {
            id: 'notes',
            className: 'tab-olfactory-notes',
            label: 'Discover.Pdp.productInfo.productTab.label.olfactoryNotes',
            value: dto.notes,
        },
    ];
    const filteredTabs = tabs
        .map((tab) =>
            tab.value
                ? createTabParam(
                      tab.id,
                      tab.className,
                      tab.label,
                      tab.value,
                      tab.displayContentInProductPage,
                      tab.additionalContents,
                  )
                : undefined,
        )
        .filter((tab): tab is ProductInfoTabParamsValue => tab !== undefined);

    return filteredTabs.map((tab, index) => ({
        ...tab,
        index,
    }));
};
